import React, { useState } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  useTheme,
  Grid,
  Button,
  Modal,
  TablePagination,
  IconButton,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Divider,
  Stack
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// --- Helper Functions ---
const getOrderTypeChip = (typeCode) => {
  switch (typeCode) {
    case 0: return { label: "In-Store", color: "info" };
    case 1: return { label: "Pickup", color: "primary" };
    case 2: return { label: "Kiosk", color: "secondary" };
    case 3: return { label: "Online", color: "warning" };
    default: return { label: "Unknown", color: "default" };
  }
};

const getOrderStatusChip = (statusCode) => {
  switch (statusCode) {
    case 1: return { label: "Active", color: "primary" };
    case 2: return { label: "Completed", color: "success" };
    case 5: return { label: "Cancelled", color: "error" };
    case 0: 
    default: return { label: "In Progress", color: "info" };
  }
};

const OrdersDetails = ({ orders, showPagination = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Changed to 'md' so tablets also get cards if space is tight, or keep 'sm'
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);

  // --- Handlers ---
  const handleOpen = (order) => {
    setSelectedOrder(order);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedOrder(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

 
  const visibleOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const cellStyle = { fontWeight: 'bold', whiteSpace: 'nowrap' };

  return (
    <Container maxWidth={false} sx={{ width: "100%", mt: { xs: 2, md: 5 }, p: { xs: 1, md: 3 } }}>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Orders ({orders.length})
        </Typography>
      </Box>

     
      <Paper elevation={isMobile ? 0 : 2} sx={{ backgroundColor: isMobile ? 'transparent' : 'background.paper' }}>
        
        {/* === MOBILE CARD VIEW === */}
        {isMobile ? (
          <Stack spacing={2}>
            {visibleOrders.length > 0 ? (
              visibleOrders.map((order, index) => {
                const orderType = getOrderTypeChip(order.type);
                const orderStatus = getOrderStatusChip(order.oStatus);
                const paymentSuccessful = order.payment && order.payment.length > 0;

                return (
                  <Card key={index} elevation={2} sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ pb: 1 }}>
                      {/* Card Header: Order No & Status */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" component="div">
                          #{order.orderNo}
                        </Typography>
                        <Chip label={orderStatus.label} color={orderStatus.color} size="small" />
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      {/* Card Body Details */}
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">Type</Typography>
                          <Box>
                             <Chip label={orderType.label} color={orderType.color} size="small" variant="outlined" />
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary" align="right" display="block">Amount</Typography>
                          <Typography variant="subtitle1" align="right" fontWeight="bold">
                            ${(order.total ?? 0).toFixed(2)}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="textSecondary">
                                    {new Date(order.startTime).toLocaleString()}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 1 }}>
                             <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="textSecondary">Payment:</Typography>
                                <Chip 
                                    label={paymentSuccessful ? "Paid" : "Pending"} 
                                    color={paymentSuccessful ? "success" : "warning"} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                             </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                    
                    {/* Card Action: View Button */}
                    <CardActions>
                      <Button 
                        fullWidth 
                        variant="contained" 
                        size="small" 
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpen(order)}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                );
              })
            ) : (
                <Typography variant="body1" align="center" color="textSecondary" sx={{ py: 4 }}>
                    No orders found.
                </Typography>
            )}
          </Stack>
        ) : (
          /* === DESKTOP TABLE VIEW === */
          <TableContainer component={Box} sx={{ maxHeight: 600, overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                  <TableCell sx={cellStyle}>Order No</TableCell>
                  <TableCell sx={cellStyle}>Type</TableCell>
                  <TableCell sx={cellStyle}>Status</TableCell>
                  <TableCell sx={{...cellStyle, textAlign: 'right'}}>Amount</TableCell>
                  <TableCell sx={cellStyle}>Payment Status</TableCell>
                  <TableCell sx={cellStyle}>Date/Time</TableCell>
                  <TableCell sx={cellStyle}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleOrders.length > 0 ? (
                  visibleOrders.map((order, index) => {
                    const orderType = getOrderTypeChip(order.type);
                    const orderStatus = getOrderStatusChip(order.oStatus);
                    const paymentSuccessful = order.payment && order.payment.length > 0;
                    const total = order.total ?? 0;

                    return (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          backgroundColor: order.oStatus === 5 ? theme.palette.error.light + '15' : 'inherit',
                          '&:hover': { backgroundColor: theme.palette.grey[50] },
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{order.orderNo}</TableCell>
                        <TableCell>
                          <Chip label={orderType.label} color={orderType.color} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip label={orderStatus.label} color={orderStatus.color} size="small" />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          ${total.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={paymentSuccessful ? "Successful" : "Pending"}
                            color={paymentSuccessful ? "success" : "primary"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(order.startTime).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Button onClick={() => handleOpen(order)} variant="outlined" color="primary" size="small">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="textSecondary" sx={{ py: 2 }}>
                        No Orders to Display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}


        {showPagination && orders.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={orders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
          />
        )}
      </Paper>

     
      <Modal open={open} onClose={handleClose} aria-labelledby="order-details-modal">
        <Box
          sx={{
            position: "absolute",
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '85%', md: '700px' },
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 2, md: 4 },
            borderRadius: 2,
            outline: 'none'
          }}
        >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ borderBottom: '1px solid #ccc', pb: 1 }}>
                <Typography id="order-details-modal" variant="h6" component="h2">
                    Order Details: {selectedOrder?.orderNo}
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

          {selectedOrder && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary" display="block">Date</Typography>
                  <Typography variant="body2">{new Date(selectedOrder.startTime).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary" display="block">Time</Typography>
                  <Typography variant="body2">{new Date(selectedOrder.startTime).toLocaleTimeString()}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                   <Typography variant="caption" color="textSecondary" display="block">Status</Typography>
                  <Chip size="small" label={getOrderStatusChip(selectedOrder.oStatus).label} color={getOrderStatusChip(selectedOrder.oStatus).color} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary" display="block">Total</Typography>
                  <Typography variant="subtitle1" color="success.main" fontWeight="bold">${selectedOrder.total?.toFixed(2) ?? '0.00'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Customer & Staff</Typography>
              <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: "#f9f9f9" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><b>Name:</b> {selectedOrder.custInfo?.name || "N/A"}</Typography>
                    <Typography variant="body2"><b>Phone:</b> {selectedOrder.custInfo?.phone || "N/A"}</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}><b>Address:</b> {selectedOrder.custInfo?.address || "N/A"}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {selectedOrder.orders?.length > 0 ? selectedOrder.orders.map((orderItem, orderIndex) => (
                      <Typography variant="body2" key={'staff' + orderIndex}><b>Staff:</b> {orderItem.cName}</Typography>
                    )) : <Typography variant="body2"><b>Staff:</b> N/A</Typography>}
                    <Typography variant="body2"><b>Payment:</b> {selectedOrder.payment?.length > 0 ? 'Successful' : 'Pending'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>Items</Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto", maxHeight: '300px' }}>
                <Table size="small" stickyHeader>
                  <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
                    <TableRow>
                      <TableCell><b>Item Name</b></TableCell>
                      <TableCell align="right"><b>Qty</b></TableCell>
                      <TableCell align="right"><b>Price</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.orders?.map((orderItem, orderIndex) => (
                      orderItem.order.map((item, idx) => (
                        <TableRow key={`${orderIndex}-${idx}`} hover>
                          <TableCell sx={{ minWidth: '150px' }}>
                            {item.item}
                            {item.comment && (
                                <Box component="span" display="block" color="text.secondary" fontSize="0.75rem" sx={{ fontStyle: 'italic' }}>
                                    * {item.comment}
                                </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">${(item.quantity * item.price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))
                    ))}
                    {!selectedOrder.orders?.some(o => o.order?.length > 0) && (
                      <TableRow><TableCell colSpan={3} align="center">No Items Found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
             <Button variant="contained" color="secondary" onClick={handleClose}>
                Close
             </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default OrdersDetails;